import React, { Fragment, useEffect, useState } from "react";
import { Col, DatePicker, Flex, Form, Input, Row, Select, Space } from "antd";
import "@/styles/components/personalizedCall.scss";
import Container from "@/components/container/Container";
import CardComponent from "@/components/card";
import { useDispatch } from "react-redux";
import { setCurrentForm, setFormProps, setModalOpen } from "@/reducers/commonSlice";
import { setContatctUs } from "@/services/users/setContatctUs";
import ButtonComponent from "@/components/button/Button";
import { countryList } from "@/helpers/countryList";
import { keyFilterOption } from "@/helpers";
import dayjs from "dayjs";
import SEOHead from "@/components/SEO/SEOHead";

// API functions for fetching options
import { getDisciplines } from "@/services/common/getDisciplines";
import { getUniversityByCountryAndRegion } from "@/services/common/getUniversityByCountryAndRegion";

const { Option } = Select;

const customizeRequiredMark = (label, { required }) => (
  <>
    {label}
    {required && <span style={{ color: "red" }}>*</span>}
  </>
);

const filterOption = (input, option) =>
  (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

const PersonalizedCall = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  // State for controlling conditional "Other" inputs
  const [isOtherCourseSelected, setIsOtherCourseSelected] = useState(false);
  const [isOtherUniversitySelected, setIsOtherUniversitySelected] = useState(false);

  // States for fetched options
  const [courseOptions, setCourseOptions] = useState([]);
  const [universityOptions, setUniversityOptions] = useState([]);

  // State for tracking selected country (for fetching universities)
  const [selectedCountry, setSelectedCountry] = useState(null);

  // Fetch Preferred Course options on mount
  useEffect(() => {
    getDisciplines()
      .then((res) => {
        const options = res.map((item) => ({
          label: item.name,
          value: item.id,
        }));
        setCourseOptions(options);
      })
      .catch((error) => console.error(error));
  }, []);

  // When a country is selected, fetch Preferred University options (without region)
  useEffect(() => {
    if (selectedCountry) {
      getUniversityByCountryAndRegion(selectedCountry, "")
        .then((res) => {
          const options = res.map((uni) => ({
            label: uni.name,
            value: uni.id,
          }));
          setUniversityOptions(options);
          form.resetFields(["university", "otherUniversity"]);
        })
        .catch((error) => console.error(error));
    }
  }, [selectedCountry, form]);

  // Handler for Preferred Country change
  const handleCountryChange = (value) => {
    setSelectedCountry(value);
  };

  // Handler for Preferred Course change
  const handleCourseChange = (value) => {
    if (value === "other") {
      setIsOtherCourseSelected(true);
    } else {
      setIsOtherCourseSelected(false);
      form.setFieldsValue({ otherCourse: "" });
    }
  };

  // Handler for Preferred University change
  const handleUniversityChange = (value) => {
    if (value === "other") {
      setIsOtherUniversitySelected(true);
    } else {
      setIsOtherUniversitySelected(false);
      form.setFieldsValue({ otherUniversity: "" });
    }
  };

  const handleFinish = (value) => {
    // Format mobile number
    if (value.phoneCode) {
      value.mobileNumber = value.phoneCode + " " + value.mobileNumber;
      delete value.phoneCode;
    } else {
      value.mobileNumber = "+91 " + value.mobileNumber;
      delete value.phoneCode;
    }
    const formData = {
      name: value.name,
      email: value.email,
      mobileNumber: value.mobileNumber,
      countryCode: value.countryCode,
      // Use the custom input if "Other" was selected
      course: value.course === "other" ? value.otherCourse : value.course,
      university: value.university === "other" ? value.otherUniversity : value.university,
      location: value.location || "",
      intake: value.intake ? dayjs(value.intake).format("YYYY") : "",
    };
    setContatctUs(formData)
      .then(() => {
        dispatch(setCurrentForm("successModal"));
        dispatch(setModalOpen(true));
        dispatch(
          setFormProps({
            message: "Your request has been registered, we will contact you soon",
            btnLabel: "Close",
            onSubmit: () => {
              dispatch(setModalOpen(false));
            },
          })
        );
        form.resetFields();
      })
      .catch((error) => {
        console.error(error);
      });
  };

  // SEO metadata for Personalized Call page
  const seoTitle = "Schedule a Personalized Call | Inforens";
  const seoDescription =
    "Book a personalized consultation call with our education experts. Get tailored advice on studying abroad, university applications, visa processes, and more.";
  const seoKeywords =
    "personalized call, education consultation, study abroad advice, university application help, Inforens consultation, expert mentoring, international education";

  // Base URL for SEO tags
  const baseUrl = import.meta.env.VITE_URL || window.location.origin;
  const pageUrl = `${baseUrl}/personalised-call`;

  return (
    <Fragment>
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        ogTitle={seoTitle}
        ogDescription={seoDescription}
        ogImage={`${baseUrl}/assets/images/personalised-call-banner.jpg`}
        ogUrl={pageUrl}
        ogType="website"
      />
      <div className="personalized-call-wrapper">
        <h2 className="title title-h4 text-center">Personalized Call Service - Inforens</h2>
        <div className="text-center">Get Abroad Education Queries Solved By Current Students</div>
        <Flex align="center" justify="space-around" className="call-wrapper">
          <div className="content">
            <h2 className="text-center" style={{ fontWeight: "600" }}>
              If studying abroad is your dream, making it a reality is ours.
            </h2>
          </div>
          <div className="img-wrapper">
            <img src="/assets/images/video-call.svg" />
          </div>
        </Flex>

        <Flex gap={"10px"} vertical>
          <div>
            <h2
              style={{ fontWeight: "500" }}
              className="title common-padd-top common-padd-bottom title-3 text-center"
            >
              What is the Personalized Call Service?
            </h2>
            <div>
              <b>Personalized Consultation Call (30 mins):</b> Connect with our expert student
              mentors to resolve all your queries about abroad education.
            </div>
            <div>
              <b>Enquire Now:</b> Start your journey by getting detailed answers to your questions.
            </div>
          </div>

          <Container className="wrapper-form">
            <CardComponent>
              <div className="inner-card">
                <CardComponent>
                  <div className="contact-us-header">
                    <div className="body-b1">
                      Book a FREE call to get started with your International Journey
                    </div>
                  </div>

                  <div className="contact-form">
                    <Form
                      name="personalizedCall"
                      layout="vertical"
                      form={form}
                      onFinish={handleFinish}
                      requiredMark={customizeRequiredMark}
                    >
                      <Row justify="center" gutter={[16, 0]}>
                        <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                          <Form.Item
                            label="Name"
                            name="name"
                            rules={[
                              {
                                required: true,
                                message: "Please enter your name!",
                              },
                            ]}
                          >
                            <Input placeholder="Enter your name" />
                          </Form.Item>
                        </Col>
                        <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                          <Form.Item
                            label="Email"
                            name="email"
                            rules={[
                              {
                                type: "email",
                                required: true,
                                message: "Please enter your email!",
                              },
                            ]}
                          >
                            <Input placeholder="Enter your email" />
                          </Form.Item>
                        </Col>
                        <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                          <Form.Item label="Phone Number">
                            <Space.Compact style={{ width: "100%" }}>
                              <Form.Item name="phoneCode" noStyle>
                                <Select
                                  virtual={false}
                                  showSearch
                                  placeholder="Select Country"
                                  defaultValue="+91"
                                  filterOption={keyFilterOption}
                                  style={{ maxWidth: "35%" }}
                                >
                                  {countryList.map((country) => (
                                    <Option
                                      key={country.name}
                                      value={country.phoneCode}
                                      label={country.phoneCode}
                                    >
                                      <Flex justify="start" align="center" gap="5px">
                                        <img src={country.flag} height={30} width={30} alt="" />
                                        {window.innerWidth > 425 ? (
                                          <span>{country.phoneCode}</span>
                                        ) : null}
                                      </Flex>
                                    </Option>
                                  ))}
                                </Select>
                              </Form.Item>
                              <Form.Item
                                name="mobileNumber"
                                noStyle
                                rules={[
                                  {
                                    min: 7,
                                    max: 14,
                                    required: true,
                                    message: "Please provide a valid mobile number with WhatsApp.",
                                  },
                                ]}
                              >
                                <Input
                                  type="number"
                                  style={{ width: "80%" }}
                                  placeholder="Enter your mobile number"
                                  onKeyPress={(event) => {
                                    if (!/[0-9]/.test(event.key)) {
                                      event.preventDefault();
                                    }
                                  }}
                                />
                              </Form.Item>
                            </Space.Compact>
                          </Form.Item>
                        </Col>
                        <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                          <Form.Item
                            label="Preferred Country"
                            name="countryCode"
                            rules={[
                              {
                                required: true,
                                message: "Please select a preferred country!",
                              },
                            ]}
                          >
                            <Select
                              virtual={false}
                              showSearch
                              placeholder="Select a Preferred Country"
                              className="w-100"
                              filterOption={filterOption}
                              onChange={handleCountryChange}
                              getPopupContainer={() => document.getElementById("select")}
                            >
                              {countryList.map((country) => (
                                <Option
                                  key={country.name}
                                  value={country.isoCode}
                                  label={country.name}
                                >
                                  <Flex justify="start" align="center" gap="5px">
                                    <img src={country.flag} height={30} width={30} alt="" />
                                    <span>{country.name}</span>
                                  </Flex>
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        {/* Preferred Course as Select with Other option */}
                        <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                          <Form.Item
                            label="Preferred Course"
                            name="course"
                            rules={[
                              {
                                required: true,
                                message: "Please select your course",
                              },
                            ]}
                          >
                            <Select
                              virtual={false}
                              placeholder="Select your course"
                              onChange={handleCourseChange}
                              showSearch
                              filterOption={filterOption}
                            >
                              {courseOptions.map((course) => (
                                <Option key={course.value} value={course.label}>
                                  {course.label}
                                </Option>
                              ))}
                              <Option value="other">Other</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                          <Form.Item
                            label="Other Course"
                            name="otherCourse"
                            rules={[
                              {
                                required: isOtherCourseSelected,
                                message: "Please enter your course name",
                              },
                            ]}
                          >
                            <Input
                              placeholder="Enter your course name"
                              disabled={!isOtherCourseSelected}
                            />
                          </Form.Item>
                        </Col>
                        {/* Preferred University as Select with Other option */}
                        <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                          <Form.Item
                            label="Preferred University"
                            name="university"
                            rules={[
                              {
                                required: true,
                                message: "Please select your university",
                              },
                            ]}
                          >
                            <Select
                              virtual={false}
                              placeholder="Select your university"
                              onChange={handleUniversityChange}
                              showSearch
                              filterOption={filterOption}
                            >
                              {universityOptions.map((uni) => (
                                <Option key={uni.value} value={uni.label}>
                                  {uni.label}
                                </Option>
                              ))}
                              <Option value="other">Other</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                          <Form.Item
                            label="Other University"
                            name="otherUniversity"
                            rules={[
                              {
                                required: isOtherUniversitySelected,
                                message: "Please enter your university name",
                              },
                            ]}
                          >
                            <Input
                              placeholder="Enter your university name"
                              disabled={!isOtherUniversitySelected}
                            />
                          </Form.Item>
                        </Col>
                        <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                          <Form.Item label="Preferred Location" name="location">
                            <Input placeholder="Enter your location" />
                          </Form.Item>
                        </Col>
                        <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                          <Form.Item label="Intake" name="intake">
                            <DatePicker
                              picker="year"
                              placeholder="select the year"
                              disabledDate={(current) => {
                                const currentYear = dayjs().year();
                                return (
                                  current &&
                                  (current.year() < currentYear || current.year() > currentYear + 2)
                                );
                              }}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row justify="center">
                        <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                          <ButtonComponent
                            label="Book a Call Now!"
                            varient="primary"
                            htmlType="submit"
                            className="w-100"
                          />
                        </Col>
                      </Row>
                    </Form>
                  </div>
                </CardComponent>
              </div>
            </CardComponent>
          </Container>
          <div>
            <h2
              className="title common-padd-top common-padd-bottom title-3 text-center"
              style={{ fontWeight: "500" }}
            >
              What Will Be the Outcome of the Personalized Call?
            </h2>
            <div className="title title-h6 common-padd-bottom" style={{ fontWeight: "450" }}>
              During the call, you will receive comprehensive guidance on:
            </div>
            <div>
              <b>Guidance from Current Students:</b> Get insights from students at your dream
              university.
            </div>
            <div>
              <b>Course Details:</b> Understand the specifics of the course you wish to pursue.
            </div>
            <div>
              <b>Job Opportunities & Internships:</b> Learn about potential career paths after
              completing your course.
            </div>
            <div>
              <b>Scholarship Information:</b> Explore available scholarship options.
            </div>
            <div>
              <b>University Campus & Facilities:</b> Get insights from students at your dream
              university.
            </div>
            <div>
              <b>Accommodation Information:</b> Find out about pocket-friendly accommodation
              options.
            </div>
            <div>
              <b>Clubs & Societies:</b>Learn about extracurricular activities at your chosen
              university.
            </div>
          </div>
          <div>
            <h2
              className="title common-padd-top common-padd-bottom title-3 text-center"
              style={{ fontWeight: "500" }}
            >
              Why Choose Us?
            </h2>
            <div className="title title-h6">
              Our current student experts provide quick guidance on:
            </div>
            <div>Selecting the right country, course, university, or college.</div>
            <div>Understanding budget and finances for studying abroad.</div>
            <div>Navigating cultural differences and overcoming language barriers.</div>
          </div>
        </Flex>
      </div>
    </Fragment>
  );
};

export default PersonalizedCall;
